/**
 * A token for the support API, for "Contact support" (Jon, 2026-09-27).
 *
 * The portal's own session is a cookie on auth's origin, and the support API is
 * another origin that cookie does not reach. So support takes a bearer token
 * whose audience is support's (`<issuer>/app/support`), minted for this portal
 * as the `account` client (auth's `CROSS_CLIENT_RESOURCE_GRANTS`). Getting one
 * is a silent OAuth round trip: authorize with `prompt=none`, which the signed-
 * in session answers without showing anything, then a PKCE code exchange.
 *
 * ── PORTED FROM THE ADMIN PORTAL ────────────────────────────────────
 *
 * RidgeWill/org-admin's `src/lib/fleet-token.ts` does exactly this for the CI
 * fleet and for support's staff routes, and is where the reasoning for each
 * guard lives. The differences here:
 *
 *   - the client is `account`;
 *   - this portal routes on the PATHNAME, not the hash, so the page to return
 *     to is `pathname + search`, and it is only ever a same-origin path;
 *   - there is one resource, so there is no dispatcher between sessions.
 *
 * `/callback` is this portal's registered redirect (auth's `clients.ts`), and
 * nothing else here uses it. `completeSupportCallback` runs in main.tsx BEFORE
 * the first render, so the app never draws a screen for `/callback`.
 */
import { LOGIN_URL } from './auth-client.js';

export const CLIENT_ID = 'account';
export const CALLBACK_PATH = '/callback';
export const DEFAULT_RETURN = '/support';

/** Renewed a minute early, so a request never sets off with a token about to lapse. */
export const EXPIRY_SKEW_MS = 60_000;

/**
 * No second redirect within this long of the last. A round trip that came back
 * without a token and tried again at once would loop between here and auth for
 * ever; this turns that into a sentence on the screen.
 */
export const REDIRECT_GUARD_MS = 10_000;

const PENDING_KEY = 'rw-account-support-pending';
const REDIRECTED_AT_KEY = 'rw-account-support-redirected-at';

export type TokenSource = () => Promise<string>;

interface Pending {
  verifier: string;
  state: string;
  returnTo: string;
}

interface Held {
  token: string;
  expiresAt: number;
}

// ── PKCE (RFC 7636) ──────────────────────────────────────────────────

function base64url(bytes: Uint8Array): string {
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** 32 random bytes, base64url: 43 characters, inside RFC 7636's 43 to 128. */
export function randomString(): string {
  return base64url(crypto.getRandomValues(new Uint8Array(32)));
}

export async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(digest));
}

export function authorizeUrl(p: { loginUrl: string; origin: string; state: string; challenge: string }): string {
  const query = new URLSearchParams({
    response_type: 'code',
    client_id: CLIENT_ID,
    redirect_uri: `${p.origin}${CALLBACK_PATH}`,
    scope: 'openid',
    // Never a sign-in form. No session means `login_required` comes straight
    // back, and the portal's own session gate is what sends somebody to sign in.
    prompt: 'none',
    state: p.state,
    code_challenge: p.challenge,
    code_challenge_method: 'S256',
    resource: `${p.loginUrl}/app/support`,
  });
  return `${p.loginUrl}/api/auth/oauth2/authorize?${query}`;
}

/**
 * Where to come back to: this portal's own path, never anything else.
 *
 * Read back from session storage after a redirect, so it is treated as
 * untrusted: a value that is not a plain same-origin path (`//evil.test`, a
 * full URL) falls back to the Support screen rather than being navigated to.
 */
export function safeReturn(value: string | undefined | null): string {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) return DEFAULT_RETURN;
  return value;
}

// ── THE SESSION ──────────────────────────────────────────────────────

export interface SupportSessionDeps {
  /** The hot-path auth origin: the issuer, and where authorize and token live. */
  loginUrl: string;
  now: () => number;
  store: () => Storage | undefined;
  location: () => Pick<Location, 'origin' | 'pathname' | 'search'>;
  /** Leaves the page. */
  navigate: (url: string) => void;
  /** Rewrites the address bar without leaving. */
  replaceUrl: (url: string) => void;
  fetch: typeof fetch;
}

export function createSupportSession(deps: SupportSessionDeps) {
  const resource = `${deps.loginUrl}/app/support`;
  let held: Held | null = null;
  /** Why the last round trip failed, kept for the life of the page: redirecting
   *  again would ask the same question and get the same answer. */
  let failure: string | null = null;
  /** Shared by concurrent callers, so two panels asking at once make one trip. */
  let renewing: Promise<string> | null = null;

  const token: TokenSource = async () => {
    if (held && deps.now() < held.expiresAt - EXPIRY_SKEW_MS) return held.token;
    held = null;
    if (failure) throw new Error(failure);
    if (renewing) return renewing;

    const store = deps.store();
    if (!store) throw new Error('This browser is blocking session storage, which contacting support needs.');
    const last = Number(safeGet(store, REDIRECTED_AT_KEY));
    if (Number.isFinite(last) && deps.now() - last < REDIRECT_GUARD_MS) {
      throw new Error('Connecting to support came back without a sign-in. Reload the page to try again.');
    }

    renewing = (async () => {
      const verifier = randomString();
      const state = randomString();
      const here = deps.location();
      const pending: Pending = { verifier, state, returnTo: safeReturn(`${here.pathname}${here.search}`) };
      try {
        store.setItem(PENDING_KEY, JSON.stringify(pending));
        store.setItem(REDIRECTED_AT_KEY, String(deps.now()));
      } catch {
        renewing = null;
        throw new Error('This browser is blocking session storage, which contacting support needs.');
      }
      deps.navigate(
        authorizeUrl({ loginUrl: deps.loginUrl, origin: here.origin, state, challenge: await challengeFor(verifier) }),
      );
      // Never settles: the page is unloading.
      return new Promise<string>(() => {});
    })();
    return renewing;
  };

  /**
   * Finish a round trip, before the app renders. A no-op anywhere but
   * `/callback`, and it never throws: a failure is kept and reported by the
   * next `token()` call, on the screen that asked.
   */
  async function completeCallback(): Promise<void> {
    const here = deps.location();
    if (here.pathname !== CALLBACK_PATH) return;

    const store = deps.store();
    const pending = store ? readPending(store) : null;
    if (store) safeRemove(store, PENDING_KEY);

    try {
      const { code, verifier } = parseCallback(here.search, pending, deps.loginUrl);
      const res = await deps.fetch(`${deps.loginUrl}/api/auth/oauth2/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          code,
          redirect_uri: `${here.origin}${CALLBACK_PATH}`,
          client_id: CLIENT_ID,
          code_verifier: verifier,
          resource,
        }),
        // The code and verifier are the proof. The cookie adds nothing here.
        credentials: 'omit',
      });
      const body = (await res.json().catch(() => ({}))) as {
        access_token?: string;
        expires_in?: number;
        error?: string;
        error_description?: string;
      };
      if (!res.ok || !body.access_token) {
        throw new Error(
          `Support could not be reached: ${body.error_description || body.error || `HTTP ${res.status}`}.`,
        );
      }
      held = { token: body.access_token, expiresAt: deps.now() + (body.expires_in ?? 0) * 1000 };
      if (store) safeRemove(store, REDIRECTED_AT_KEY);
    } catch (e) {
      failure = e instanceof Error ? e.message : String(e);
    }
    // Back to where the trip started, whatever happened, so the address bar never
    // keeps a spent code and a reload does not replay it.
    deps.replaceUrl(safeReturn(pending?.returnTo));
  }

  return { token, completeCallback };
}

/** The code and its verifier, or a thrown sentence saying why there are none. */
export function parseCallback(search: string, pending: Pending | null, issuer: string): { code: string; verifier: string } {
  const q = new URLSearchParams(search);
  if (!pending || !q.get('state') || q.get('state') !== pending.state) {
    // Not ours: a stale tab, another tab's round trip, or a forged link.
    throw new Error('A sign-in response arrived that this tab did not ask for. Reload the page to try again.');
  }
  // RFC 9207: a response naming another issuer is a mix-up, whatever it carries.
  const iss = q.get('iss');
  if (iss && iss !== issuer) throw new Error(`A sign-in response came from ${iss}, not ${issuer}. It was refused.`);
  const error = q.get('error');
  if (error === 'login_required') {
    throw new Error('You are no longer signed in, so support cannot be reached. Sign out and sign in again.');
  }
  if (error) throw new Error(`Connecting to support failed: ${q.get('error_description') || error}.`);
  const code = q.get('code');
  if (!code) throw new Error('The sign-in response carried no code. Reload the page to try again.');
  return { code, verifier: pending.verifier };
}

function readPending(store: Storage): Pending | null {
  try {
    const raw = store.getItem(PENDING_KEY);
    const parsed = raw ? (JSON.parse(raw) as Partial<Pending>) : null;
    return parsed?.verifier && parsed.state && typeof parsed.returnTo === 'string' ? (parsed as Pending) : null;
  } catch {
    return null;
  }
}

function safeGet(store: Storage, key: string): string | null {
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
}

function safeRemove(store: Storage, key: string): void {
  try {
    store.removeItem(key);
  } catch {
    // A leftover stamp lapses after REDIRECT_GUARD_MS.
  }
}

export const supportSession = createSupportSession({
  loginUrl: LOGIN_URL,
  now: () => Date.now(),
  store: () => {
    try {
      return window.sessionStorage;
    } catch {
      return undefined;
    }
  },
  location: () => window.location,
  navigate: (url) => window.location.assign(url),
  replaceUrl: (url) => window.history.replaceState(null, '', url),
  fetch: (...args) => fetch(...args),
});

export const supportToken: TokenSource = supportSession.token;
